import { Helmet } from 'react-helmet-async'
import { BreadcrumbsJsonLd } from '@/components/seo/Breadcrumbs'
import { LocalBusinessJsonLd } from '@/components/seo/LocalBusiness'
import { motion } from 'framer-motion'
import { Hero } from "@/components/sections/hero"
import { Welcome } from "@/components/sections/welcome"
import { About } from "@/components/sections/about"
import { CTA } from "@/components/sections/cta"
import { CTA15Min } from "@/components/sections/cta-15min"
import { WhatWeHelpWith } from "@/components/sections/what-we-help-with"
import { FAQ } from "@/components/sections/faq"
import { AppointmentCards } from "@/components/sections/appointment-cards"
import { getGeneralHealthAppointments } from "@/data/appointments"
import { LocationsSection } from "@/components/sections/locations-section"
import { WhatToExpect } from "@/components/sections/what-to-expect"
import { Button } from "@/components/ui/button"
import { CalendarPlus, ArrowRight } from "lucide-react"
import { Link } from "react-router-dom"
import { trackEvent } from "@/lib/analytics"

export default function HomePage() {

  const openBooking = (source?: string) => {
    if (source) {
      trackEvent('book_appointment_click', { location: source })
    }
    // Use the global booking modal from Navigation component
    if ((window as any).openBookingModal) {
      ;(window as any).openBookingModal(source)
    }
  }

  const generalHealthAppointments = getGeneralHealthAppointments()

  return (
    <main className="min-h-screen bg-background">
      <Helmet>
        <title>Raintree Chinese Medicine | Acupuncture & Women's Health in Noosa & Maroochydore | Sunshine Coast</title>
        <meta name="description" content="Gentle acupuncture and Chinese herbal care for women's health, fertility, pregnancy and menopause with Dr Renee Knott at Raintree Chinese Medicine clinics in Noosa and Maroochydore, serving the Sunshine Coast, Queensland." />
        <meta name="keywords" content="acupuncture Noosa, acupuncture Maroochydore, Chinese medicine Sunshine Coast, women's health acupuncture, fertility acupuncture, pregnancy acupuncture, Raintree Chinese Medicine, Dr Renee Knott, AHPRA registered acupuncturist, Noosa Heads, Tewantin, Maroochydore, Queensland" />
        <link rel="canonical" href="https://raintreechinesemedicine.com.au/" />
        <meta property="og:title" content="Raintree Chinese Medicine | Acupuncture & Women's Health in Noosa & Maroochydore | Sunshine Coast" />
        <meta property="og:description" content="Gentle acupuncture and Chinese herbal care for women's health, fertility, pregnancy and menopause with Dr Renee Knott at Raintree Chinese Medicine clinics in Noosa and Maroochydore, serving the Sunshine Coast, Queensland." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://raintreechinesemedicine.com.au/" />
        <meta property="og:image" content="https://tdjwqdpgnmzhfcqsfyjh.supabase.co/storage/v1/object/sign/images/general/Raintree-Chinese-Medicine-Renee.png?token=eyJraWQiOiJzdG9yYWdlLXVybC1zaWduaW5nLWtleV8wYzVlMGM1NS1lYzI2LTRkMTYtOTc2MS1iZDQ1MWJhMGJiYjEiLCJhbGciOiJIUzI1NiJ9.eyJ1cmwiOiJpbWFnZXMvZ2VuZXJhbC9SYWludHJlZS1DaGluZXNlLU1lZGljaW5lLVJlbmVlLnBuZyIsImlhdCI6MTc2NTE4MzkxOCwiZXhwIjoyMDIxNzkxOTE4fQ.-w4pg5Ka_Qt5M94A-DmuvJDiGP7a4DljS5sOLKvfAfE" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Raintree Chinese Medicine | Acupuncture & Women's Health in Noosa & Maroochydore | Sunshine Coast" />
        <meta name="twitter:description" content="Gentle acupuncture and Chinese herbal care for women's health, fertility, pregnancy and menopause with Dr Renee Knott at Raintree Chinese Medicine clinics in Noosa and Maroochydore, serving the Sunshine Coast, Queensland." />
      </Helmet>
      <h1 className="sr-only">Raintree Chinese Medicine – Acupuncture in Noosa & Maroochydore, serving the Sunshine Coast</h1>
      <BreadcrumbsJsonLd />
      <LocalBusinessJsonLd />
      <Hero />
      
      {/* New Hero Section */}
      <section className="bg-[rgb(132,137,116)] py-16 lg:py-40 px-4 lg:px-0">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-6xl mx-auto"
          >
            {/* Mobile: Stack layout */}
            <div className="lg:hidden text-center">
              <h1 className="text-2xl md:text-3xl mb-4 text-white">
                Acupuncture & Chinese Medicine for Women's Health in Noosa & Maroochydore, serving the Sunshine Coast
              </h1>
              <p className="text-white/90 sm:w-2/3 mx-auto mb-6 sm:mb-12 text-sm sm:text-base">
                Raintree Chinese Medicine offers gentle, long-term care for cycles, fertility, pregnancy and menopause – with acupuncture, Chinese herbal medicine and nervous system support.
              </p>
              <div className="flex flex-col gap-3 mb-4 sm:w-1/2 mx-auto">
                <Button 
                  className="w-full bg-white text-gray-800 hover:bg-gray-100"
                  onClick={() => openBooking('noosa_clinic')}
                >
                  <span className="inline-flex items-center gap-2">
                    <CalendarPlus className="h-4 w-4" aria-hidden="true" />
                    Book Noosa Clinic
                  </span>
                </Button>
                <Button 
                  className="w-full bg-white text-gray-800 hover:bg-gray-100"
                  onClick={() => openBooking('maroochydore_clinic')}
                >
                  <span className="inline-flex items-center gap-2">
                    <CalendarPlus className="h-4 w-4" aria-hidden="true" />
                    Book Maroochydore Clinic
                  </span>
                </Button>
              </div>
              <Button
                variant="outline"
                className="w-full border-white text-white hover:bg-white/10 sm:w-1/2 mx-auto"
                onClick={() => openBooking('free_consult_header')}
              >
                <span className="inline-flex items-center gap-2">
                  <CalendarPlus className="h-4 w-4" aria-hidden="true" />
                  Free 15min Phone Consult
                </span>
              </Button>
            </div>

            {/* Desktop: Side-by-side layout */}
            <div className="hidden lg:grid lg:grid-cols-2 lg:gap-12 lg:items-center">
              <div>
                <h1 className="text-3xl lg:text-4xl mb-4 text-white">
                  Acupuncture & Chinese Medicine for Women's Health in Noosa & Maroochydore, serving the Sunshine Coast
                </h1>
                <p className="text-white/90 text-base lg:text-lg">
                  Raintree Chinese Medicine offers gentle, long-term care for cycles, fertility, pregnancy and menopause – with acupuncture, Chinese herbal medicine and nervous system support.
                </p>
              </div>
              <div className="flex flex-col gap-3 mx-auto px-4">
                <Button 
                  className="w-full bg-white text-gray-800 hover:bg-gray-100"
                  onClick={() => openBooking('noosa_clinic')}
                >
                  <span className="inline-flex items-center gap-2">
                    <CalendarPlus className="h-4 w-4" aria-hidden="true" />
                    Book Noosa Clinic
                  </span>
                </Button>
                <Button 
                  className="w-full bg-white text-gray-800 hover:bg-gray-100"
                  onClick={() => openBooking('maroochydore_clinic')}
                >
                  <span className="inline-flex items-center gap-2">
                    <CalendarPlus className="h-4 w-4" aria-hidden="true" />
                    Book Maroochydore Clinic
                  </span>
                </Button>
                <Button
                  variant="outline"
                  className="w-full border-white text-white hover:bg-white/80"
                  onClick={() => openBooking('free_consult_header')}
                >
                  <span className="inline-flex items-center gap-2">
                    <CalendarPlus className="h-4 w-4" aria-hidden="true" />
                    Free 15min Phone Consult
                  </span>
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <Welcome />
      <About />
      <CTA15Min />
      <WhatWeHelpWith onBook={(areaId) => openBooking(`what_we_help_with_${areaId}`)} />
      <LocationsSection onBook={(locationName) => {
        trackEvent('book_appointment_click', { location: `homepage_locations_${locationName.toLowerCase()}` })
        openBooking(`homepage_locations_${locationName.toLowerCase()}`)
      }} />
      <WhatToExpect />
      <section id="appointments-section" className="py-20">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <h2 className="text-2xl md:text-3xl mb-4 text-[#3f4141] max-w-[900px] mx-auto">
              Appointments & Fees
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Appointments are available in both Noosa and Maroochydore clinics.<br/>
              If you're booking for the first time, please choose an Initial Consultation & Treatment.
            </p>
          </motion.div>
          <AppointmentCards 
            appointments={generalHealthAppointments} 
            onBook={openBooking} 
          />
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mt-8 lg:mt-12"
          >
            <p className="text-gray-600 max-w-2xl mx-auto mb-2">
              All current fees and availability are shown in the online booking system.
            </p>
            <div className="flex justify-center">
              <Link
                to="/appointments"
                className="text-[#c5a574] hover:text-[#DBB981] font-medium inline-flex items-center gap-1 transition-colors text-base"
              >
                Learn more about Appointments and Fees
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </motion.div>
        </div>
        </section>
        <FAQ />
      <CTA />
    </main>
  )
}